import { math, size } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { Port } from '../../fsm/types';
import { responsive } from '../helpers/mixins';
import { color, measurement } from '../helpers/vars';

interface PortInfoProps {
  readonly cx: number;
  readonly cy: number;
}

interface RouteProps {
  readonly from: Port;
  readonly to: Port;
}

interface LinkProps {
  readonly path: string;
  readonly forwards: string;
  readonly backwards: string;
}

interface RouteInfoProps extends RouteProps, LinkProps {}

export interface RouteMapProps {
  readonly origin: Port;
  readonly destination: Port;
  readonly isCruise?: boolean;
}

const PortInfo: { readonly [key in Port]: PortInfoProps } = {
  [Port.DETRV]: { cx: 810, cy: 630 },
  [Port.FIHEL]: { cx: 1110, cy: 330 },
  [Port.FILAN]: { cx: 999, cy: 356 },
  [Port.FINLI]: { cx: 1036, cy: 330 },
  [Port.SEKPS]: { cx: 966, cy: 370 },
  [Port.SEMMA]: { cx: 858, cy: 550 },
  [Port.PLSWI]: { cx: 902, cy: 630 },
};

enum Link {
  HANSA,
  FINN,
  NORDO,
  POLISH,
}

const LinkPath = {
  [Link.HANSA]: {
    path: ['M1110 330c3 18-44 36-65 66-28 37-18 91-51 121-44 40-65 31-118 57-32 15-64 41-66 56'],
    forwards: ['M1041 394l7-2 1 7M986 519l7-1 1 7M879 566l7 2-2 7'],
    backwards: ['M1053 392l-7 1-1-7M999 518l-7 1-1-7M892 572l-7-2 2-7'],
  },
  [Link.FINN]: {
    path: ['M999 356s13 0 24-7c12-7 13-19 13-19', 'M966 370s9 2 19-2 14-12 14-12'],
    forwards: ['M1025 353l-7-1 2-7', 'M991 371l-6-3 3-6'],
    backwards: ['M1012 350l7 1-2 7', 'M980 365l6 3-3 6'],
  },
  [Link.NORDO]: {
    path: ['M810 630c1-18 15-26 31-43 9-11 16-26 17-37'],
    forwards: ['M831 590h7v7'],
    backwards: ['M844 591h-7v-7'],
  },
  [Link.POLISH]: {
    path: ['M898 618c-4-28-35-36-40-68'],
    forwards: ['M879 595l1-7 7 1'],
    backwards: ['M881 582v7l-7-1'],
  },
};

const getPaths: (link: Link, i?: number) => LinkProps = (link, i = undefined) => {
  const getOne: (dArr: string[]) => string = (dArr) => {
    return i !== undefined ? dArr[i] : dArr.join('');
  };

  const currentLink = LinkPath[link];

  return Object.keys(currentLink).reduce(
    (acc, key) => ({
      ...acc,
      [key]: getOne(currentLink[key as keyof LinkProps]),
    }),
    {}
  ) as LinkProps;
};

const RouteInfo: RouteInfoProps[] = [
  {
    from: Port.DETRV,
    to: Port.FIHEL,
    ...getPaths(Link.HANSA),
  },
  {
    from: Port.DETRV,
    to: Port.SEMMA,
    ...getPaths(Link.NORDO),
  },
  {
    from: Port.FINLI,
    to: Port.FILAN,
    ...getPaths(Link.FINN, 0),
  },
  {
    from: Port.FINLI,
    to: Port.SEKPS,
    ...getPaths(Link.FINN),
  },
  {
    from: Port.FILAN,
    to: Port.SEKPS,
    ...getPaths(Link.FINN, 1),
  },
  {
    from: Port.PLSWI,
    to: Port.SEMMA,
    ...getPaths(Link.POLISH),
  },
];

const MapContainer = styled.div`
  ${size(measurement.size.map.container)}
  margin: ${measurement.space.departure.area.s} auto;
  position: relative;

  ${responsive.fontSize(css`
    margin: ${measurement.space.departure.area.l} auto;
  `)}

  &::after {
    display: block;
    height: ${measurement.size.map.actual.h};
    width: 100vw;
    background: ${color.bg.default};
    position: absolute;
    top: 50%;
    left: ${math(`${measurement.size.map.actual.w} / 2`)};
    transform: translate(0, -50%);
    content: '';
  }
`;

const SVG = styled.svg`
  ${size(measurement.size.map.actual.h, measurement.size.map.actual.w)}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const MapGroup = styled.g`
  fill: ${color.bg.default};
`;

const Map: React.FunctionComponent = () => (
  <MapGroup>
    <path d="M1318 35c4-9 10-19 10-35h592v960h-339c12-5 26-9 39-12 5-11 9-7 14-2 5 4 10 9 19 4 8-8 12-5 16 0 4 3 7 6 13 5 6-8 14-6 23-5 8 2 16 3 22-4 66 3 166-87 63-110-18 6-35-1-51-8s-33-14-51-9c-15-7-18-5-21-4-4 2-6 3-20-8-22 3-19-1-15-4 2-3 4-5-6-3 3-4 8-4 12-5 5 0 9 0 13-4v-8c0-15 0-30 16-28 8-4-4-5-7-5-4-2-10-4-13-1-4 0-10-8-8-8 3 0 5-2 7-5 2-2 4-5 6-3 2 1 7 0 7-3l-3-1-4-1 10-9c6-5 12-9 9-14-4-7-10-1-16 5-5 4-10 8-14 8-2 2-5 4-9 5-6 2-13 5-12 12-6 2-15 9-23 17-13 11-25 22-30 14-6 1 2 9 6 10-6 1-7 2-7 3s0 3-3 5c-2 1-3 3-3 6-1 4-2 7-8 3-3-1-5 0-7 1l-6 2c-2 2-4 1-7 1s-5-1-5 2c27-1 38 7 46 12 10 7 15 11 35-8 14-7 14-4 14 2l1 10c-10 7-17 6-21 6-5 0-7 0-9 8-2 4-7 7-11 10-7 3-13 7-14 15-18 22-21 13-24 1l-6-14h-23c-14 1-19 1 3-21 23-20 6-16-12-11-9 2-18 5-23 4-14 0-12-2-11-4 2-2 3-4-12-3l12-3 10-3h-2c-4-1-13-4-15-12 2 9-6 12-13 16-8 3-17 7-18 17 1 9-2 7-6 4-3-2-8-6-10 1 16 5 11 11 5 18-3 3-6 7-7 11l-3-3c0-1-1-3-2-2-1 4 2 9 5 15 5 9 11 20-2 24l-5 4-4 3v-1c0-2 0-6-2-10-10 1-4 14-1 19-5 9-3 14-2 20 2 4 3 8 3 14 6 9 2 11-4 13-4 2-9 4-11 9H950c-8-11-16-17-24 0h-13c-10-11-5-16-1-19 3-2 5-4 0-7-4-2-5-1-6 0s-3 2-9 0c-4 0-8 3-12 6-4 4-8 7-13 8 7-11-4-2-8 2-2 3 1 7 4 10H424c2-11 5-26 12-21 15 33 16 9 0-8h-1c-4-1-7-2-4-6-8-13-5-11-1-8s9 7 3-7c5-4 1-6-5-9-7-2-15-6-16-15-7-4-5-6-2-10l4-4c-6-1-8-3-3-7h3l4 1c-1-5-5-4-9-3l-6 1c-6-5-4-5-3-6 1 0 2 0 1-2 2-3-5-3-9-3s-6 0 1-2c1-3-2-3-5-2h-7c-3-5-8-7-14-9l-10-5c-5-2-9 0-10 1-2 1-2 1-2-5-14-6-4-8 4-5 6-4-4-7-7-6-2-4 1-3 4-2 3 0 6 1 6-1-2-2-6-3-9-4-7-2-11-4 3-8 4-3 9-2 13-1 3 0 6 1 10-1 3 4 6 2 8-1 2-2 5-4 9-1 6 0 7 4 9 7 1 3 2 6 5 8l4-3c3-2 6-5 8-2 2 4 5 3 8 1 3-1 5-3 6 2 17 6 12-10 8-24-3-9-5-17-3-19 3 5 10 4 15 3 4-1 6-1 2 4 0 9 9 10 18 11l8 1c4 6 26-3 18-4 0-10 10-12 20-15 16-4 32-9 27-36l1-1c20-7 44-15 66-15-5-1-10-2-7-6 6-2 14 2 17 6h-4l7 5v-3-6c-9-3-7-4-3-5 4 0 8-1 10-4-17-5-13-8-8-13 3-3 7-6 9-11 3-1 3-7 4-11 1-6 2-12 7-10 3-2 3-1 3 0l2 3c2 1 6 3 4 6-31 30 46 16 5-8-5-10 13-23 25-19 8-5 12-2 16 1 3 2 5 4 9 5 4-2 2-4 0-5-3-1-5-2-2-4 1-12 32-13 27 1 7 3 7 0 7-3-1-2-1-4 4-2 2-18 16-10 24-5 5 2 9 4 7-1-2-4-10-6-15-8s-8-3-3-4c1-3 0-4-1-4l-4-2c5-3 7-7-3-4-3-2 1-4 4-5l3-2c3-3 0-5-3-7-5-4-5-10-4-16 0-5 0-9-2-13-5-1-5-2-5-4-1-1-1-2-3-2-3-1-3 0-3 1s0 2-2 1v-8-6c2 5 9-4 3-7-2-1-2 0-2 1-1 1-1 2-2 1v-1h-1c0-11 1-10 3-10 1 1 2 1 3-2-4-3-9-11-2-13-4 4 5 5 8 4l1 1 2 2c3-6 12-19 9-3 1 1 4-1 3-3 1-1 2 1 2 2v2c5 0 4-3 2-6s-3-5-2-6c1-6 4-6 7-6s6 0 9-2c-2-3-7-2-13 0l-11 2c-12 5-11 7-9 11v4c-11-5-7-13 2-19 2 1 5 0 7-1l7-2c6-1 9-5 12-9 4-4 7-9 14-9 2-2 4-5 7-4-3 4-2 9-1 13 1 7 3 14-12 14 9 4 8 6 7 9 0 2-1 4 1 6-2 1-4 8 1 2 2 4 4 4 7 3s5-1 8 1c2 2 0 4-2 6l-2 6-2-1c0-1 0-2-2-2l-1 1c-1 2-2 3-3 0 2-2 3-3-1-3-5 6-4 9-3 11 0 3 1 4-7 7 7 6-5 6-9 5 8 2 4 5 1 6-3 2-5 3 1 4-2 1-1 4 0 6l1 3c-8 4-6 5-2 8 1 1 4 3 4 5h-2c-2 0-5 0-4 3 3-1 6 0 8 2 8-1 5 3 2 5h2c1 0 3 0 2 2-10 6 2 5 4 4l1 2 2 1c2-3 5-1 7 0 2 2 5 4 9 1 9-4 6 1 3 6s-5 9-2 9c9-3 11-1 13 0h4c2-5 8-10 15-10l5-4c5-5 12-13 16-7-4 0-11 3-3 3 4-1 8 1 12 3 4 3 9 5 15 4 2 10 8 12 15 15 4 1 8 2 11 5-3-15 11-21 24-27 8-3 15-6 21-11l7-5c19-12 40-25 61-8-1 1-3 0-6-1-2-2-5-4-8-2-4 8 11 11 19 11 9-5 13-9 14-10l-7 12c7 0 12-7 16-13l6-8c-12 4-12 2-10-8 10 3 16-13 19-19 2-4 2-5 1 4-12 14 17 17 8 3-1-9-5-19-10-28-7-16-15-31-4-48v-4c0-12 0-14 15-21h1c2-3 5-6 9-4 0 5 4 6 9 8 4 1 9 2 10 7 0 13 17 11 24 3 6-11 4-14 0-18-3-5-8-11-3-31-4-2-6-1-8 1l-4 3c-7-2-19-12-13-17-6 0-7-1-7-6-4-10-1-11 4-14 3-2 7-4 11-10l5-1c2-1 4-1 3-4l6-1c5 0 11 0 10-6 6-3 15-2 24-1 13 0 26 1 31-8l-1-2c-1-3-1-7 3-7h5c0-4 2-5 4-5 2-1 6-2 11-10 3-2 7-2 11-2 5 0 11-1 11-4-8-5-14-4-19-3s-9 2-15-3c-7 0-7-2-6-5 0-2 0-5-4-8-8 10-19 14-30 18-8 2-16 5-23 9v1c-1 2-1 4-4 4-5-1-9 3-14 6-2 2-5 5-9 6-2 4-8 6-14 8l-11 5-3 3c-2 2-4 4-6 3 4-5 2-7-1-10-2-3-5-5-4-10-2 0-3 1-5 3-3 3-6 5-9-2h-4c-4-1-10-2-12-5-1-1-2 0-3 1s-2 2-4 1c-6-7-6-16-6-25 0-8 0-17-6-24-5-1-6-5-6-10 0-4-1-9-6-11-6-6-3-13-1-19 1-4 3-8 1-11 8-1 22-4 10-13 4 0 5-2 5-5v-4c10 0 16-15 20-30 5-12 9-24 16-25h2c2 0 4 1 3-1-5-2-5-8-6-13s-1-10-6-10c-4-3-6-3-9-4-2 0-4-1-7-4l-13 4c-5 0-9 1-14 4l-1-1c-4-2-9-5-7 1l-2-1h-2c-2 4 0 6 1 7s2 2 1 4c-5 0-9 2-8 6l-4 2h-2c0 2 1 3 2 3l2 4-3 13c-2 6-3 7 10 18l-11 17c-5 11-10 22-23 24l-3 6c-1 3-2 7-6 4-4 2-6 7-7 11-2 4-3 8-9 9 3 5 2 14-7 14-11-7-2 9 3 11-4 4-4 10-2 15l2 1c1 2 3 4 0 5-12-5 0 26 4 33-3 5 0 6 4 7 3 0 6 1 7 3 3-4 5-4 7 0 4 4 6 5 8 5 2 1 3 1 4 6 6 3 8 2 9 2l7 7c-4 1-7 5 0 4l-20 17c-6 3-6 0-6-4-1-2-1-5-4-5v3l-1 4c-3-2-4-1-5-1-1 1-3 2-6 0-5 0-31 4-22 6 4 2 8 1 11 0s5-2 7-1c13 9 24 5 38-3 4 3 0 5-4 5-1 1 1 2 2 2 1 1 2 1 1 2-4 1-6 4-7 7-2 4-4 7-8 1l-2 3c-1 3-1 5-6 7-3 0-4 2-5 3-2 2-3 4-8 4 8 33 6 66-7 101-3-4-10-3-17-1-4 1-8 2-11 1-1 3-3 4-6 6-2 2-5 4-6 8-1 2 0 4 1 7 2 3 3 7-2 9-5-1-10 1-16 2-10 3-18 5-14-10-1-3-5-6-9-10-7-7-14-14 0-11-2-7-2-8 0-8 1 0 3-1 3-7-5-1-17-14-18-19l-3-5c-1-3-1-5-7-3l-1-6c0-3 1-7-5-7l1-10c2-9 4-18-7-13-7-3-7-11-8-18-1-8-2-15-9-15-6-3-6-12-6-19-1-7-1-11-6-4 4 9 2 8-1 8-2 0-4 0 2 10l-2 4c-4 5-8 12-14 8l-14 13c-15 16-33 36-58 21 1 8-9 2-4-2-26-8-32-39-20-27 5 0 6-2 7-6 0-2 1-5 4-8-3-1-2-4-1-6 1-1 1-2-3 1-3-6-3-5-5-2l-2 5-2-1c-6-2-8-3-9 7-5 2-2-22 6-20-2 7-1 7 0 5s4-6 7-3l7-6h1c-14 1-12-1-7-4 4-3 10-7 7-12-4-4-5 0-6 4-1 2-2 4-4-3-3 1-2-2-1-4 1-1 2-3 1-4-8 15-17 1-7-4 4 8 12-4 9-9l-6 2c-4 2-9 5-12-4h2l4 2c2 1 4 2 1-2 1-2 0-2-2-2l-3-1c-1-3 0-4 1-5s1-1-2-3c10 2 17 1 23 0 9-2 15-3 23 3 0-2 4-3 7-5 3-1 5-2-4-2-7-4-15-2-23 0-9 2-17 4-23-3-2-6 2-7 5-8 1 0 2 0-2-2 3-3 1-4-1-5l-3-2c8-10 10-9 14-7 2 1 5 3 11 2-7-7-12-6-15-6-3 1-4 1-7-8 2-1 4 1 5 3l3 2c2-1 1-2 0-2-1-1-2-2-1-3 4 2 5 2 6 1 2 0 3-1 10 3-9-9 4-13 6-3 0-8 1-13 11-8 0-5-4-4-7-4-4 1-8 1-7-4 3-3 11-2 17 0 6 1 11 2 9-2 12-5 8-5 2-4h-4c4-3 3-2-1-2-2 1-6 2-8 1-1-14 23-7 32 3 3-1 0-4-4-7l-6-8c2-1 5 1 6 4 3 3 5 6 6 0 3-2 1-2-2-3-3 0-6-1-5-3 9 0 9-1 8-3s-2-4 4-8c6-2 5-1 5 2v2c4 1 12-4 4-5 1-4 4-7 10-7 3 10 4 10 6 8 3-2 7-5 17-4-3-4 1-6 5-8 3-2 6-4 6-7-6 1-4-2-2-4 2-3 5-6-4-5l-7 6c-4 4-7 6 3 3 1 5-23 18-21 8 5-6-2-4-6-1-3 0 1-5 3-6 6-1 5-3 5-5s0-4 4-7c5-16 13-13 19-12 4 2 7 2 4-9 6-2 6-12-2-5-1-6 7-8 15-10 7-2 15-4 13-10l-3 3-3 2-1-1-1-6 2-2c1-1 2-2 1-4-8-4-4-8 0-13 3-3 6-6 4-9h-3c-2 0-3 0-1-3 5 0 12-1 13-6-4 1-4 0-1-1-8-2-8-2-3-7l2-2c-4-3-1-5 2-8l4-2c-5-3-2-5 2-6 4-2 8-4 7-7 2-4 9-3 13-3 5 1 8 2 4-3-32 2-7-15 3-2-1-4-1-8 2-11-12 5-10-2-3-8-5-4-7-1-9 1s-3 4-5 3c-1-7 6-8 13-10 6-1 13-2 13-7-16-13 6-12 12-10-2-2 0-3 2-4l2-1c0-5-3-4-7-2-3 1-7 3-11 2-4-6 4-8 10-9 4-1 7-2 1-3 7-2 7-2 6-3-1 0-3-1 1-4-1-3-2-1-4 0-1 1-2 2-2-1 1-6 3-7 6-7 2-1 5-2 2-12 2-1 4 1 6 2 3 1 5 3 6 0-17-13 6-6 7 2 7 0 4-3 0-5-3-3-7-5-4-7-3-3-3-6-1-7h7c2 2 4 5 1 12 5 1 5-4 4-8V1l1-1h6c0 5 0 12-3 16 6-1 6-7 6-13V0h383c5 39-47 51-89 52l-8-2c-4-2-6-3-10 3-2 0-2-1-2-2v-1h-9c-3-1-6-1-10 1-5 0-9-2-13-4-5-3-10-6-18-1 7-1 8 1 10 4 1 1 2 3 5 4l7 2c-1 1-1 2 7 1-1 2 2 3 4 4 3 1 7 3 7 6h8c6 0 12 0 19 3 6 5 11 12 9 19-6 4-1 7 4 11 4 2 9 5 10 9l7 8 6 6 6 1 18 1c3-1 4 0 6 2s5 5 15 1c9 5 13-1 17-7l2-3-5-6c-5-6-5-7-17-1l-12-5c-9-3-18-6-10-13-8-9 12-8 33-8 22 1 46 2 43-7-41-20-36-28-28-43zM1038 575zM727 544l1 3-1-3z" />
    <path d="M885 9c7 4 16-10 4-9 2 2 1 3-1 5-1 1-3 2-3 4zM138 124c-3 5-13 1-16-1-5 7 0 8 7 10l6 2c11 2 18 10 25 18 9 12 19 23 40 20 10-6 26-7 40-9l27-4c7 4 13 0 19-5 4-3 9-6 15-7v-1c0-1 0-2 3-2v-3c-1-2-2-3 2 0 6 1 5-2 5-7-1-3-1-8 3-11l-5-5c-2-3-5-5-4-8-10 0-8-2-5-5 3-2 7-6-2-8 0-6 7-9 12-11-21 4-21 1-20-3 0-4 1-8-11-11l-3 6c-1 5-3 10-9 6-2-6-4-4-7-1-3 2-6 5-9 3-8-23-10-8-11 4l-1 4c-2 1-2-4-2-9 0-8-1-18-9-13-7-2-9 2-10 6-1 2-2 5-5 5-2-2-2-6-2-10 0-6-1-12-8-7-4 18-7 17-11 15-4-1-9-3-16 10-1-13 2-16 6-22l5-9c-2-1-3-4-4-7-4-10-9-21-19-15l3 2c3 0 5 1 4 5-8-4-6 0-4 4s4 9-2 8c0-24-37-10-16-1 3 2 1 2-2 2-2 0-4-1 0 1-2 3-5-1-7-4-3-3-5-7-5 1-1 3-3 2-5 1-2-2-4-3-5 1 50 16 39 20 32 23-5 1-8 3 5 6-3 6-13 3-24-1-7-2-14-5-20-4-4 7 3 8 10 9 6 1 12 2 14 5-2 7 0 10 1 12l2 6c-5 1-4 1-2 2 2 2 6 3-5 7zM510 660c30-16 58 13 28 35-3 5-7 5-10 6-4 0-6 0-3 4-1 5-4 5-9 5-3 0-6 1-8 3 96 25-87 26-112 21-6 14-14 12-23 10-10-3-22-6-34 8l-4-3c-2-2-4-4-10-2 3-5 10-8 17-11 9-4 19-8 22-16 0-4 2-4 5-4l5-1c4-7 11-6 19-5 11 2 25 4 36-17-27 14-29 13-33 9-3-2-7-6-19-7l2-3c1 0 2-1-2-2-1-5-6-3-11-2-5 2-11 4-13-2 3-4 1-6 0-7-2-2-2-3 12-6 25 1 39-34 9-28 0-3 3-4 6-5 4-2 7-3 8-6 12-6 19-5 25-3 7 1 10 2 14-17-1-3 1-5 4-7 2-3 4-5 3-8-3-1-6 1-8 2-3 2-5 3-4-4-10-16-5-23 11-29-12-4-13-3-13-1-1 1-2 3-6 3-4 3-8 1-11-1-4-2-6-4-5 2-2 4-4 2-6 0s-4-4-6-4-2 2-2 4 0 3-2 2c-10-7-3-14 4-20 6-6 12-12 6-19 1-6 2-8 4-9 1-2 3-3 0-8-1-2-2 0-2 2 0 3-1 7-4 6-2-4-3-3-4-2s-1 2-3 0c1-3 1-6-4-3-12 25 3-27 16-30-1-1-4 1-7 3-4 4-10 8-14 3l-4-4c-3-3-4-4 5-3 4-1 4-3 3-5 0-2-1-4 5-3v-5c5 2 6 0 2-2l1-3c3-3 5-6-3-6v-3c0-2-1-4 3-2 3 2 2-2 1-6 0-4-1-9 3-4 2 1 3 0 1-2-1-2 2-2 5-1 2 0 6 1 7-1-5-5-2-9 3-13 3-4 6-7 7-12 4 6 16 5 27 4 12-1 21-1 16 7 2 4-7 9-15 12-10 5-18 9-5 11-1 3-5 3-8 3-3 1-5 1-5 3 8 0 4 1 1 3l-5 2c1 2 3 1 5 0l3-1c86-18 41 42 19 47 10 6 13 9-2 9-4 5-8 5-12 5h-6c9 7 15 4 20 2 3-2 5-3 7 1 18 8 21 22 23 36 2 8 3 15 7 22 15 3 19 16 22 27l5 14c-8-4-29-6-9-1 20 12 13 20 9 25-3 3-5 5 0 8z" />
    <path d="M221 673c19 3 37-2 54-7 12-3 24-6 35-7 24 1 25-19 27-38 1-16 3-32 17-35 8 1 8 0 7-2s-2-4 0-8c7 12 8-9-4-7 12-15-11-31-31-21-2-3 2-4 4-4l3-1c-10-6-11-4-14 1l-3 5c-4 0-3-2-1-5l2-3c-1-4-7 0-7 0-9-3-12 1-15 6-1 2-2 5-4 6-18 3-3 8 7 8 0 3-5 4-10 5s-9 2-4 6c-5 2-13-1-20-4-9-4-19-7-22 1 5 1 4 4 4 7-1 3-2 5 5 5-8 5-29 19-5 21-3 6 1 6 6 6 4 0 8 0 10 3l-6 1c-8 2-9 2-12 12-19 6-13 6-2 6 8 0 18-1 21 2l-15 1c-7 0-14 0-19 4-1 7-6 6-11 2-12 1-6 8 3 8 1 2-2 3-6 4s-9 3-3 6c5 3 9 3 10 3h0l-8 5h8l6 1-3 3-4 4zM451 737c-6 2 3 5 5 4 12-4 0-6-5-4zM378 529c-2 4-10 24-13 14l4-6c2-4 4-9 9-8zM356 445c3 3 5 0 8-2l3-3c4 2 5 0 7-3s3-5 8-4c2-1 0-2-2-2h-1l1-2c2-2 4-6 0-7-6 5-11 6-14 7-5 1-8 2-9 8 3 4 2 5 1 5-1 1-2 1-2 3zM376 467c10 4 2 13-2 11 4-6 3-6 1-5-3 0-8 0-10-10-8-3-5-3-1-4 4 0 9-1 7-5 3-1 3 2 3 5-1 3-1 7 2 8zM374 505c5-2-11-16-12-8 6 4 4 5 2 6-2 0-4 1-4 3h8c2-1 4-2 6-1z" />
    <path d="M239 577c3 5 4-2 2-4-4-1-7 2-2 4zM657 684c3-2 7-7 1-7-8 1-13 10-1 7zM847 38l-1 1c-2-2 1-7 3-10l2-4c7 0 0 9-4 13zM839 50c1 13 10-13-1-12 2 3-1 6-4 7-3 2-6 4-2 6 1-1 11-5 7-1zM856 48c3-17 15-5 0 10-1-4-1-3-2-1s-2 7-5 4c5-9-4-4-5 2-1-5 5-20 7-25 2 1 2 3 2 6 0 2 1 5 3 4zM867 31c12 4 27-24 6-17-4 4-4 6-3 9 0 2 0 5-3 8zM350 527c6-2 7-2 4 4 13 6 2-22-4-4zM400 287c10 6-5-19-4-9 2 3 4 6 4 9zM465 416c-19-5-7-12-4-7-1 2 3 4 5 5s4 2-1 2zM503 367c0 4 2 5 3 5 2 0 4 0 1 12 11-10 8-40-4-17zM389 630c-4 2-6 4-8-1-1-10 8-7 12-1l-4 2zM384 597c5 0 11-7 11-11-4-2-19 8-11 11zM659 400c-1 1-1 2-3 2l-1-4c-1-4-1-6 6 0l-2 2zM731 253c-8 9 27-5 9-3-3-2-4-1-5 0 0 2-1 3-4 3zM823 586c12 9-11 8-16 3 3-1 3-2 2-2l-1-3c3-4 5-2 7 1 2 1 4 3 6 2h2v-1zM912 574c8-3 3-6-2-8l-5-3c-7 3-1 11 7 11zM999 363c-3 2-8-9-2-7 3-2 2-4-2-5 1-5 12 3 10 7-4-4-4-3-4-1 1 2 1 5-2 6zM946 510c3-13 9-31 4-31l-5 16c-4 9-8 19-2 26l3-11z" />
    <path d="M829 589c-9-6 0-9 6-4l-3 7c-1 4-2 6-3-3zM890 617c-4 4 8 5 11 3 0-3-20-16-14-7 5-1 10 6 3 4zM876 603c-6 4-13 0-11-7l3-4c1-2 2-3 5 2 5-1 5 0 5 2s0 4 3 6c1 2-3 3-5 1zM1077 394c-5-2-7-1-10 1-2 2-4 4-9 4l6 2h3l3 2c1 2 2 4 4 3l3-4c2-3 4-5 0-8zM1056 356c6 2 8-5 8-8-7 1-9 2-8 8zM1067 436c2 2 3-1 4-4s2-6 4-7c6-2 10-6 12-11 8-5-15-7-21 1-1 3-2 3-4 2-1 0-2 0-3 3 1 4 4 6 5 7 3 1 4 2 3 9zM984 489c-10 4-11-41 7-37 7-1 5 1 3 5-2 3-4 7-1 10 3 5 1 7-3 10-3 2-6 5-6 12zM812 550c-1 2-3 2-5 2l-5 1c4 1 6 7 8 10-3 7 3 8 9 9h5l1 2c2 4 4 8 10 4-2-6 2-8 7-11h1l-4-5c-3-2-5-4 4-9 3-9-4-24-15-12 5 1 7 11 3 14l-2-6c0-4-1-4-4 5-3-2-3-4-3-6-1-3-1-5-7-4 1 3 1 4-1 5l-2 1zM744 515c5-7 1-9-4-2-1 3 2 6 4 2zM788 559c-30-2-5 27 10 18v-5c1-3 2-5-1-8 0-2 1-4-3-5 0 2-1 4-4 4-1 1-1 0-1-1 1-1 1-3-1-3z" />
  </MapGroup>
);

interface RoutePathProps {
  isVisible?: boolean;
  isDirection?: boolean;
}

const RoutePath = styled.path<RoutePathProps>`
  stroke: ${color.brand};
  stroke-width: 3;

  ${(props) =>
    !props.isVisible &&
    css`
      display: none;
    `}

  ${(props) =>
    !props.isDirection &&
    css`
      stroke-dasharray: 0 10;
    `}
`;

interface PortCircleProps extends PortInfoProps {
  readonly isOrigin?: boolean;
  readonly isDestination?: boolean;
}

const PortCircle = styled.circle<PortCircleProps>`
  ${(props) =>
    props.isOrigin || props.isDestination
      ? css`
          fill: ${color.bg.default};
          stroke: ${color.brand};
          stroke-width: 2;
        `
      : css`
          fill: ${color.brand};
        `}
`;

const PortMarker: React.FunctionComponent<PortCircleProps> = ({ isOrigin, isDestination, ...props }) => (
  <PortCircle {...{ isOrigin, isDestination, ...props }} r={isDestination ? 9 : 3} />
);

const RouteMap: React.FunctionComponent<RouteMapProps> = ({ origin, destination }) => {
  return (
    <MapContainer>
      <SVG viewBox="0 0 1920 960" xmlns="http://www.w3.org/2000/svg" key={`${origin}-${destination}`}>
        <Map />
        {RouteInfo.map((r) => {
          const forwards = origin === r.from && destination === r.to;
          const backwards = origin === r.to && destination === r.from;

          return (
            <React.Fragment key={`${r.from}-${r.to}`}>
              <RoutePath d={r.path} isVisible={forwards || backwards} />
              <RoutePath d={r.forwards} isDirection isVisible={forwards} />
              <RoutePath d={r.backwards} isDirection isVisible={backwards} />
            </React.Fragment>
          );
        })}
        {Object.entries(PortInfo).map(([port, info]) => {
          return <PortMarker key={port} {...info} isOrigin={origin === port} isDestination={destination === port} />;
        })}
      </SVG>
    </MapContainer>
  );
};

export default RouteMap;
